import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getUserGridLayout,
  saveUserGridLayout,
  resetUserGridLayout,
} from '../../../services/userGridLayoutService';

const useGridLayoutSync = ({ enabled = false, gridKey, gridState, defaultPageSize = 25 }) => {
  const [loaded, setLoaded] = useState(!enabled);
  const [saving, setSaving] = useState(false);
  const appliedRef = useRef(false);

  const safeArray = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') {
      try {
        const parsed = JSON.parse(v);
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const safeObject = (v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) return v;
    if (typeof v === 'string') {
      try {
        const parsed = JSON.parse(v);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!enabled || !gridKey || appliedRef.current) return;

    let cancelled = false;

    (async () => {
      try {
        const layout = await getUserGridLayout(gridKey);

        if (!cancelled && layout) {
          const columnOrder = safeArray(layout.ColumnOrder);
          const sortModel = safeArray(layout.SortModel);
          const columnVisibility = safeObject(layout.ColumnVisibility);
          const columnWidths = safeObject(layout.ColumnWidths);
          const pinnedColumns = safeObject(layout.PinnedColumns);

          if (columnOrder?.length) gridState.setColumnOrder(columnOrder);
          if (columnVisibility) gridState.setColumnVisibilityModel(columnVisibility);
          if (columnWidths) gridState.setColumnWidths(columnWidths);
          if (pinnedColumns?.left?.length || pinnedColumns?.right?.length) {
            gridState.setPinnedColumns(pinnedColumns);
          }
          if (sortModel) gridState.setSortModel(sortModel);
          if (layout.Density) gridState.setDensity(layout.Density);
          if (typeof layout.PageSize === 'number') gridState.setPageSize(layout.PageSize);
        }
      } catch (err) {
        console.error('Failed to load saved grid layout', err);
      } finally {
        if (!cancelled) {
          appliedRef.current = true;
          setLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, gridKey]);

  const saveLayout = useCallback(async () => {
    if (!enabled || !gridKey) return;
    setSaving(true);

    try {
      await saveUserGridLayout(gridKey, {
        ColumnOrder: gridState.columnOrder,
        ColumnVisibility: gridState.columnVisibilityModel,
        ColumnWidths: gridState.columnWidths,
        PinnedColumns: gridState.pinnedColumns,
        SortModel: gridState.sortModel,
        Density: gridState.density,
        PageSize: gridState.pageSize,
      });
    } catch (err) {
      console.error('Failed to save grid layout', err);
    } finally {
      setSaving(false);
    }
  }, [enabled, gridKey, gridState]);

  const resetLayout = useCallback(async () => {
    if (!enabled || !gridKey) return;
    setSaving(true);

    try {
      await resetUserGridLayout(gridKey);

      gridState.setColumnOrder([]);
      gridState.setColumnVisibilityModel({});
      gridState.setColumnWidths({});
      gridState.setPinnedColumns({ left: [], right: [] });
      gridState.setSortModel([]);
      gridState.setDensity('compact');
      gridState.setPageSize(defaultPageSize);
    } catch (err) {
      console.error('Failed to reset grid layout', err);
    } finally {
      setSaving(false);
    }
  }, [enabled, gridKey, gridState]);

  return { loaded, saving, saveLayout, resetLayout };
};

export default useGridLayoutSync;